import {ReactNode} from 'react'
import {Trans} from 'react-i18next'
import {List, Row, RowLabel, RowValue, Section, SectionHeading} from './KpiDetailList.styled'

type KpiMessage = {i18nKey: string; defaults: string}

/** One label/value pair. `value` is a node so callers can pass a formatted string or a translated term. */
export type DetailRow = {
  /** Stable key + test id for the row. */
  id: string
  /** The row's label message (translated). */
  label: KpiMessage
  /** The row's value — a preformatted string, or a `<Trans>` for a translatable term like a role. */
  value: ReactNode
}

type KpiDetailListProps = {
  /** The section's heading message (e.g. "Offer Details"). */
  heading: KpiMessage
  /** The rows to render, in display order. */
  rows: DetailRow[]
  /** Stable test id for the section wrapper. */
  testId: string
}

/**
 * A titled definition list of label/value rows, shown beneath the KPI tiles (PT-5712) for the
 * Offer Details and Ownership sections. Purely presentational: every value is preformatted by the
 * caller, so the list issues no request and does no number work itself.
 *
 * @param heading - The section heading message.
 * @param rows - The label/value pairs to render.
 * @param testId - Stable data-testid for the section.
 */
export const KpiDetailList = ({heading, rows, testId}: KpiDetailListProps) => (
  <Section data-testid={testId}>
    <SectionHeading>
      <Trans {...heading} />
    </SectionHeading>
    <List>
      {rows.map((row) => (
        <Row key={row.id} data-testid={row.id}>
          <RowLabel>
            <Trans {...row.label} />
          </RowLabel>
          <RowValue>{row.value}</RowValue>
        </Row>
      ))}
    </List>
  </Section>
)
